import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "@/lib/query/keys";
import { useSession } from "@/lib/store/session";
import { canGoBack } from "@/lib/nav/back";
import {
  closeTopOverlay,
  exitApp,
  launchDeepLinkPath,
  listenForBackButton,
  listenForDeepLinks,
  listenForResume,
  setupPushNotifications,
} from "@/lib/native";

/** The launch intent is read once per WebView load; a remount must not replay it. */
let launchUrlConsumed = false;

/**
 * Capacitor wiring: deep links, resume refresh, push notifications. Renders
 * nothing; no-op on the web. Mounted from RootLayout so it is present on every
 * route, including the logged-out /auth pages the Garmin callback lands on.
 */
export function NativeBridge() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const userId = useSession((s) => s.userId);

  useEffect(() => listenForDeepLinks((path) => navigate(path)), [navigate]);
  useEffect(() => {
    if (launchUrlConsumed) return;
    launchUrlConsumed = true;
    void launchDeepLinkPath().then((path) => {
      if (path && path !== "/") navigate(path, { replace: true });
    });
  }, [navigate]);
  useEffect(
    () =>
      listenForResume(
        () =>
          void qc.invalidateQueries({
            queryKey: keys.synch(useSession.getState().userId),
          }),
      ),
    [qc],
  );
  useEffect(() => {
    if (!userId) return;
    return setupPushNotifications({
      onTap: (path) => navigate(path),
      // A push while the app is open is not shown by the OS on Android; the
      // list refreshing is how the user sees the run arrive.
      onReceived: () =>
        void qc.invalidateQueries({
          queryKey: keys.synch(useSession.getState().userId),
        }),
    });
  }, [userId, navigate, qc]);

  // Android hardware back: dismiss an open overlay first, then walk history,
  // then quit — the same rule the on-screen back control follows.
  useEffect(
    () =>
      listenForBackButton(() => {
        if (closeTopOverlay()) return;
        if (canGoBack()) navigate(-1);
        else exitApp();
      }),
    [navigate],
  );

  return null;
}
